import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageSquarePlus, Send, Upload, X, CheckCircle2, Monitor, Globe, Sparkles, Loader2, ImagePlus } from "lucide-react";

export const AdminFeedbackFloatingButton = () => {
  const location = useLocation();
  const { user, hasStaffRole, isSuperAdmin } = useAuth();
  
  // Check if admin session is unlocked or user is logged in staff
  const [isAdminUnlocked, setIsAdminUnlocked] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [formData, setFormData] = useState({
    submitter_name: "",
    submitter_email: "",
    category: "change_request",
    section_name: "",
    priority: "medium",
    description: "",
    expected_behavior: "",
    attachment_url: "",
  });

  useEffect(() => {
    const checkAdminState = () => {
      const unlocked = localStorage.getItem("admin_dashboard_unlocked") === "true";
      setIsAdminUnlocked(Boolean(user) && (hasStaffRole || isSuperAdmin));
    };

    checkAdminState();
    const interval = setInterval(checkAdminState, 2000);
    return () => clearInterval(interval);
  }, [user, hasStaffRole, isSuperAdmin]);

  // Pre-fill user name/email if logged in
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        submitter_email: prev.submitter_email || user.email || "",
        submitter_name: prev.submitter_name || user.user_metadata?.full_name || user.email?.split("@")[0] || "Admin",
      }));
    } else {
      const savedName = localStorage.getItem("admin_feedback_name");
      const savedEmail = localStorage.getItem("admin_feedback_email");
      if (savedName || savedEmail) {
        setFormData((prev) => ({
          ...prev,
          submitter_name: prev.submitter_name || savedName || "",
          submitter_email: prev.submitter_email || savedEmail || "",
        }));
      }
    }
  }, [user, isOpen]);

  // Don't render if user is not logged in / staff / admin
  if (!isAdminUnlocked) return null;

  const currentPath = location.pathname + location.search;
  const pageTitle = document.title;
  const resolution = `${window.innerWidth}x${window.innerHeight}`;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (selected.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10 MB");
      return;
    }

    setUploadingFile(true);
    try {
      const ext = selected.name.split(".").pop() || "jpg";
      const filePath = `change-requests/${Date.now()}-${Math.random().toString(36).substring(2, 7)}.${ext}`;
      
      const { error: uploadErr } = await supabase.storage
        .from("photo-submissions")
        .upload(filePath, selected);

      if (uploadErr) throw uploadErr;

      const { data: publicData } = supabase.storage
        .from("photo-submissions")
        .getPublicUrl(filePath);

      setFormData((prev) => ({ ...prev, attachment_url: publicData.publicUrl }));
      setFile(selected);
      toast.success("Screenshot uploaded");
    } catch (err: any) {
      console.error("Upload error:", err);
      toast.error("Could not upload file preview");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      toast.error("Please provide laser-specific details of what needs to change.");
      return;
    }
    if (!formData.submitter_name.trim() || !formData.submitter_email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }

    setSubmitting(true);
    try {
      localStorage.setItem("admin_feedback_name", formData.submitter_name);
      localStorage.setItem("admin_feedback_email", formData.submitter_email);

      const record = {
        page_url: currentPath,
        page_title: pageTitle,
        category: formData.category,
        section_name: formData.section_name.trim() || "General Page Section",
        priority: formData.priority,
        description: formData.description.trim(),
        expected_behavior: formData.expected_behavior.trim() || null,
        attachment_url: formData.attachment_url || null,
        screen_resolution: resolution,
        user_agent: navigator.userAgent,
        submitter_name: formData.submitter_name.trim(),
        submitter_email: formData.submitter_email.trim(),
        status: "pending",
      };

      const { error } = await supabase.from("site_change_requests").insert([record]);
      if (error) throw error;

      setSubmitted(true);
      toast.success("Laser change request submitted!", {
        description: "Your request has been logged in the Admin Change Requests tracker.",
      });

      // Reset fields after short delay
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setFile(null);
        setFormData((prev) => ({
          ...prev,
          section_name: "",
          description: "",
          expected_behavior: "",
          attachment_url: "",
        }));
      }, 1800);
    } catch (err: any) {
      console.error("Failed to submit change request:", err);
      toast.error("Could not save request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Action Trigger Button */}
      <div className="fixed bottom-5 left-5 z-[9999] print:hidden">
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full shadow-2xl bg-gradient-to-r from-amber-500 via-orange-600 to-red-600 text-white hover:brightness-110 border-2 border-white/40 px-4 py-2.5 h-auto text-xs sm:text-sm font-bold flex items-center gap-2 group transition-all duration-300 hover:scale-105"
          aria-label="Request Site Change"
        >
          <Sparkles className="h-4 w-4 animate-pulse text-yellow-200" />
          <span className="hidden sm:inline">Request Site Change</span>
          <span className="sm:hidden">Change</span>
          <Badge variant="secondary" className="bg-white/20 text-white text-[10px] uppercase font-mono border-0">
            Admin
          </Badge>
        </Button>
      </div>

      {/* Laser-Specific Request Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <MessageSquarePlus className="h-5 w-5 text-amber-500" />
              Laser-Specific Site Change Request
            </DialogTitle>
            <DialogDescription>
              Submit exact details for any text update, bug fix, layout change, or new feature needed on this page.
            </DialogDescription>
          </DialogHeader>

          {/* Current Page Auto Context Badge */}
          <div className="bg-muted/60 border border-border/80 rounded-xl p-3 text-xs space-y-1.5 font-mono">
            <div className="flex items-center gap-2 text-muted-foreground truncate">
              <Globe className="h-3.5 w-3.5 shrink-0 text-accent" />
              <span className="font-semibold text-foreground truncate">Page:</span> {currentPath}
            </div>
            <div className="flex items-center gap-2 text-muted-foreground truncate">
              <Monitor className="h-3.5 w-3.5 shrink-0 text-accent" />
              <span className="font-semibold text-foreground truncate">Viewport:</span> {resolution}
            </div>
          </div>

          {submitted ? (
            <div className="py-10 text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto animate-bounce" />
              <h3 className="text-lg font-bold text-foreground">Request Logged Successfully!</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Thank you! Your laser-specific edit request is now tracked in the Admin Change Requests dashboard.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 pt-1">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold">Your Name *</Label>
                  <Input
                    value={formData.submitter_name}
                    onChange={(e) => setFormData({ ...formData, submitter_name: e.target.value })}
                    placeholder="e.g. Coach Kasey"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold">Your Email *</Label>
                  <Input
                    type="email"
                    value={formData.submitter_email}
                    onChange={(e) => setFormData({ ...formData, submitter_email: e.target.value })}
                    placeholder="you@email.com"
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-bold">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(val) => setFormData({ ...formData, category: val })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="change_request">Text / Content Update</SelectItem>
                      <SelectItem value="bug">Bug / Broken Feature</SelectItem>
                      <SelectItem value="design">Design / Layout Fix</SelectItem>
                      <SelectItem value="feature">New Feature / Addition</SelectItem>
                      <SelectItem value="urgent_fix">Urgent Priority Fix</SelectItem>
                      <SelectItem value="other">Other Request</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-bold">Target Section / Button</Label>
                  <Input
                    value={formData.section_name}
                    onChange={(e) => setFormData({ ...formData, section_name: e.target.value })}
                    placeholder="e.g., Hero Header, Register CTA, Standings Table"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs font-bold">Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(val) => setFormData({ ...formData, priority: val })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent / Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold">Laser-Specific Change Details *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Be exact! E.g. 'Change the registration deadline text from March 28 to April 2 inside the hero banner banner card.'"
                  rows={4}
                  required
                  className="mt-1 text-sm"
                />
              </div>

              <div>
                <Label className="text-xs font-bold">Expected Behavior / Result (Optional)</Label>
                <Textarea
                  value={formData.expected_behavior}
                  onChange={(e) => setFormData({ ...formData, expected_behavior: e.target.value })}
                  placeholder="What should it look like or do after we make the fix?"
                  rows={2}
                  className="mt-1 text-sm"
                />
              </div>

              {/* Attachment / Screenshot Upload */}
              <div>
                <Label className="text-xs font-bold">Attach Screenshot / Mockup (Optional)</Label>
                <div className="mt-1 flex items-center gap-3">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                    className="text-xs"
                  />
                  {uploadingFile && <Loader2 className="h-4 w-4 animate-spin text-accent" />}
                </div>
                {formData.attachment_url && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 p-2 rounded-md">
                    <ImagePlus className="h-4 w-4 shrink-0" />
                    <span className="truncate">Screenshot attached successfully</span>
                  </div>
                )}
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold">
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Logging Request...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Change Request
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminFeedbackFloatingButton;
